/**
 * CSV to NACHA Converter App
 * 
 * This application converts CSV files into NACHA format for ACH transfers, streamlining 
 * the process of distributing funds to limited partners. The app allows users to:
 * 
 * - Upload and validate CSV files with payment information
 * - Generate NACHA formatted files according to Chase Bank specifications
 * - Preview both CSV data and resulting NACHA output
 * - Download generated NACHA files for bank submission
 * 
 * The application includes password protection, detailed validation of CSV files,
 * comprehensive help documentation, transaction summaries, and support for addenda records.
 * 
 * @author Richard McGirr
 */

import React, { useState, useEffect } from 'react';
import Papa from 'papaparse';
import { companyPresets } from './config';

/**
 * ManualPage Component
 * 
 * Provides comprehensive documentation and help information for users.
 * Implemented as a modal overlay that explains:
 * - What NACHA format is and how it's used for ACH transfers
 * - Required CSV format and field specifications
 * - Company configuration requirements
 * - Transaction ID field requirements (particularly for Chase Bank)
 * - Addenda record format and usage
 * - Troubleshooting common issues
 * 
 * @param {Function} onClose - Function to close the manual modal
 * @param {string} initialSection - Optional ID of section to scroll to on open
 */
function ManualPage({ onClose, initialSection }) {
  // Use effect to scroll to the initial section once the component is mounted
  React.useEffect(() => {
    if (initialSection) {
      setTimeout(() => {
        const element = document.getElementById(initialSection);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [initialSection]);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow max-w-4xl w-full h-[85vh] flex flex-col relative">
        {/* Header with fixed position close button */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <img 
              src="/logo.svg" 
              alt="Property Llama Capital Logo" 
              className="h-10 w-auto mr-3"
            />
            <h2 className="text-2xl font-bold">NACHA Converter Manual</h2>
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Scrollable content area */}
        <div className="flex-grow overflow-y-auto p-4 sm:p-6">
          
          {/* Table of contents for quick navigation */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="text-md font-semibold mb-2">Quick Navigation</h3>
            <div className="flex flex-wrap gap-2">
              <a 
                href="#csv-format" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("csv-format").scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs px-3 py-1 rounded-full"
              >
                CSV Format
              </a>
              <a 
                href="#transaction-id" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("transaction-id").scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs px-3 py-1 rounded-full"
              >
                Transaction ID
              </a>
              <a 
                href="#addenda" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("addenda").scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs px-3 py-1 rounded-full"
              >
                Addenda Records
              </a>
              <a 
                href="#chase-submission" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("chase-submission").scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs px-3 py-1 rounded-full"
              >
                Chase Bank Submission
              </a>
              <a 
                href="#troubleshooting" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("troubleshooting").scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs px-3 py-1 rounded-full"
              >
                Troubleshooting
              </a>
            </div>
          </div>
          
          <div className="space-y-6">
            <section>
              <h3 className="text-lg font-semibold mb-2">What is NACHA?</h3>
              <p className="text-gray-700">
                NACHA (National Automated Clearing House Association) format is used for ACH transfers between banks in the United States. 
                This tool helps you convert a CSV file containing payment information into a NACHA formatted file that can be uploaded to your 
                bank (such as Chase) for processing payments.
              </p>
            </section>
            
            <section>
              <h3 className="text-lg font-semibold mb-2">Getting Started</h3>
              <ol className="list-decimal pl-4 space-y-2 text-gray-700">
                <li><strong>Upload Your CSV File</strong> - Your file must contain the required columns (see CSV Format below).</li>
                <li><strong>Generate NACHA File</strong> - Click the button to process your CSV and create a NACHA file.</li>
                <li><strong>Download the NACHA File</strong> - Save the generated file to your computer.</li>
                <li><strong>Upload to Your Bank</strong> - Log in to your bank's portal and upload the NACHA file for processing.</li>
              </ol>
            </section>
            
            <section>
              <h3 className="text-lg font-semibold mb-2">CSV Format</h3>
              <p className="text-gray-700">Your CSV file must have the following columns:</p>
              <div className="bg-gray-100 p-3 rounded text-sm font-mono mt-1 overflow-x-auto">
                <code>transactionId,name,accountNumber,routingNumber,amount,transactionType,addenda</code>
              </div>
              <p className="mt-2 text-gray-700">Example:</p>
              <div className="bg-gray-100 p-3 rounded text-sm font-mono overflow-x-auto">
                <code>INV2025001,John Doe,12345678901234,021000021,100.50,debit,Invoice #12345</code><br/>
                <code>VEN2025002,Jane Smith,98765432101234,031100209,200.75,debit,Payment for services</code>
              </div>
              
              <div className="mt-3 bg-blue-50 border-l-4 border-blue-500 p-3 text-blue-700">
                <p><strong>Notes:</strong></p>
                <ul className="list-disc pl-4 mt-1">
                  <li>The <code>transactionId</code> field is used for tracking each payment (must be unique).</li>
                  <li>Account numbers should be provided without dashes or spaces.</li>
                  <li>Routing numbers must be valid 9-digit ABA routing numbers.</li>
                  <li>Amount should be a decimal number (e.g., 100.50 for $100.50).</li>
                  <li>Transaction type should be either "debit" or "credit".</li>
                  <li>The <code>addenda</code> field is optional and allows for payment related information (up to 80 characters).</li>
                </ul>
              </div>
            </section>
            
            <section>
              <h3 className="text-lg font-semibold mb-2">Company Configuration</h3>
              <p className="text-gray-700">
                The company configuration requires the following information:
              </p>
              <ul className="list-disc pl-4 mt-1 text-gray-700">
                <li><strong>Company Name</strong> - Your company name (max 16 characters).</li>
                <li><strong>Company ID</strong> - Your company ID or tax ID number.</li>
                <li><strong>Tax ID</strong> - Your company's tax identification number.</li>
                <li><strong>Chase Routing Number</strong> - The routing number for your bank.</li>
                <li><strong>Service Class Code</strong> - Indicates the type of entries (debits, credits, or both).</li>
              </ul>
            </section>
            
            <section id="transaction-id">
              <h3 className="text-lg font-semibold mb-2">Transaction ID Field Requirements</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  The <code>transactionId</code> field is critical for ACH transactions as it serves as the <strong>Transaction ID</strong> (Individual ID Number) in the NACHA file.
                  This field has specific requirements, especially when working with Chase Bank:
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-blue-800 text-sm mt-2">
                  <p><strong>Chase Bank Transaction ID Guidelines:</strong></p>
                  <ul className="list-disc pl-4 mt-1">
                    <li><strong>Format:</strong> The Transaction ID can contain letters, numbers, and some special characters.</li>
                    <li><strong>Length:</strong> Limited to 15 characters in the NACHA specification. Chase will truncate longer Transaction IDs.</li>
                    <li><strong>Uniqueness:</strong> While sequential numbering (001, 002, etc.) is commonly used, Chase recommends using Transaction IDs that are unique across all transactions, not just within a single batch.</li>
                    <li><strong>Persistence:</strong> Use consistent Transaction IDs for recurring payments to the same recipient.</li>
                    <li><strong>Traceability:</strong> Chase uses this field for transaction tracing and reconciliation.</li>
                    <li><strong>Returns/Rejections:</strong> If a transaction is returned or rejected, the Transaction ID is used to identify which specific transaction had the issue.</li>
                  </ul>
                </div>
                
                <div className="mt-3">
                  <p><strong>Best Practices for Transaction ID Fields:</strong></p>
                  <ul className="list-disc pl-4 mt-1">
                    <li>Use a consistent naming scheme across all your ACH transactions.</li>
                    <li>Consider including a date component (e.g., 20250323001) for chronological tracking.</li>
                    <li>For vendor payments, you might include a vendor code prefix (e.g., VEN001).</li>
                    <li>For payroll, employee IDs or encrypted identifiers are recommended.</li>
                    <li>Avoid using sensitive information like SSNs or full account numbers.</li>
                    <li>Keep a record mapping these Transaction IDs to your internal transaction records.</li>
                  </ul>
                </div>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-yellow-800 text-sm mt-3">
                  <p><strong>Important Note:</strong> Chase Bank specifically requires that you maintain records of these Transaction IDs for at least 2 years, as they may be needed for audit purposes or to resolve disputed transactions.</p>
                </div>
              </div>
            </section>
            
            <section id="addenda">
              <h3 className="text-lg font-semibold mb-2">Addenda Records</h3>
              <div className="space-y-3 text-gray-700">
                <p>
                  NACHA files support addenda records that provide additional information about a payment. This is often used 
                  for including invoice numbers, customer references, or other payment-related information.
                </p>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-yellow-800 text-sm mt-2">
                  <p><strong>Addenda Format Guidelines:</strong></p>
                  <ul className="list-disc pl-4 mt-1">
                    <li>Maximum length of 80 characters</li>
                    <li>Typically used for invoice numbers, payment references, or descriptions</li>
                    <li>The optional addenda column in your CSV creates a Type 05 addenda record in the NACHA file</li>
                    <li>Each addenda record is linked to its parent entry detail record</li>
                    <li>Some banks may have specific formatting requirements for addenda information</li>
                  </ul>
                </div>
              </div>
            </section>
            
            <section id="chase-submission">
              <h3 className="text-lg font-semibold mb-2">Chase Bank Submission Steps</h3>
              <div className="space-y-3 text-gray-700">
                <ol className="list-decimal pl-4 space-y-2">
                  <li>Download the NACHA file using the button in the application</li>
                  <li>Log in to Chase for Business or Chase Connect at chase.com/business</li>
                  <li>Navigate to ACH payment services
                    <ul className="list-disc pl-6 mt-1">
                      <li>From the Pay & transfer menu, choose ACH payment services, then ACH file upload</li>
                    </ul>
                  </li>
                  <li>Upload the .nacha file
                    <ul className="list-disc pl-6 mt-1">
                      <li>Click "Choose a file" button on the ACH file upload page</li>
                      <li>Select your downloaded .nacha file</li>
                      <li>Click "Next" to validate your file</li>
                    </ul>
                  </li>
                  <li>Review and submit the file for processing</li>
                  <li>Save or print the confirmation page for your records</li>
                </ol>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-blue-800 text-sm mt-3">
                  <p><strong>Note:</strong> Chase Bank typically processes ACH transfers within 1-2 business days. Transfers initiated before the cutoff time (usually 8:00 PM ET) will begin processing on the same business day.</p>
                </div>
              </div>
            </section>
            
            <section id="troubleshooting">
              <h3 className="text-lg font-semibold mb-2">Troubleshooting</h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>CSV File Errors:</strong> Ensure your CSV has all required columns and follows the format exactly.</p>
                <p><strong>File Not Processing:</strong> Check that your amounts are valid numbers and account/routing numbers are correct.</p>
                <p><strong>Bank Rejection:</strong> If your bank rejects the file, verify that your company information is correct.</p>
                <p><strong>Addenda Issues:</strong> If addenda records aren't appearing correctly, ensure they're under 80 characters.</p>
              </div>
            </section>
            
            <div className="text-center mt-8 pt-4 border-t">
              <p className="text-sm text-gray-500">
                For additional support, please contact your system administrator.
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Developed by Richard McGirr
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Main Application Component
 * 
 * Acts as the container for the entire CSV to NACHA converter application.
 * Handles all state management, file processing, authentication, and UI rendering.
 */
function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  // File and data state
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvFormatError, setCsvFormatError] = useState('');
  const [nachaContent, setNachaContent] = useState('');
  const [nachaPreview, setNachaPreview] = useState('');
  const [error, setError] = useState('');
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // UI state
  const [showManual, setShowManual] = useState(false);
  const [initialHelpSection, setInitialHelpSection] = useState('');
  
  // Company configuration state with default values - using fixed preset from config.js
  const [companyConfig, setCompanyConfig] = useState({
    immediateDestination: '021000021', // Chase Bank routing number
    immediateOrigin: '1234567890',     // Your company ID
    companyName: 'PROPERTY LLAMA CAP', // Company name (max 16 chars)
    companyIdentification: '1234567890', // Tax ID or company ID
    originatingDFI: '021000021',       // Your financial institution routing number
  });
  
  // Check if user is already authenticated in session storage
  useEffect(() => {
    const authenticated = sessionStorage.getItem('nacha_authenticated');
    if (authenticated === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  /**
   * Handles CSV file selection and initial validation
   * 
   * This function:
   * 1. Captures the selected CSV file
   * 2. Resets any previous state (errors, content, previews)
   * 3. Reads and parses the CSV file using PapaParse
   * 4. Validates the CSV format (required columns)
   * 5. Generates a preview of both the CSV and potential NACHA output
   * 6. Sets appropriate error states if validation fails
   * 
   * @param {Event} e - The file input change event
   */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      setNachaContent('');
      setError('');
      setCsvFormatError('');
      setNachaPreview('');
      
      // Generate preview of the CSV
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const result = Papa.parse(text, { 
            header: true, 
            preview: 5, // Show first 5 rows
            skipEmptyLines: true 
          });

          // Validate CSV format
          const requiredColumns = ['transactionId', 'name', 'accountNumber', 'routingNumber', 'amount', 'transactionType'];
          const optionalColumns = ['addenda']; // Optional column for NACHA addenda records
          const allColumns = [...requiredColumns, ...optionalColumns];
          
          const missingColumns = requiredColumns.filter(col => !result.meta.fields.includes(col));
          
          // Check for any unknown columns that might indicate a formatting error
          const unknownColumns = result.meta.fields.filter(col => !allColumns.includes(col));
          
          if (missingColumns.length > 0) {
            setCsvFormatError(`Invalid CSV format. Missing required columns: ${missingColumns.join(', ')}`);
            setCsvPreview(result); // Still show preview to help user diagnose issues
            setTransactionSummary(null);
          } else if (unknownColumns.length > 0) {
            setCsvFormatError(`Warning: Unknown columns detected: ${unknownColumns.join(', ')}`);
            setCsvPreview(result);
            
            // Calculate transaction summary even with warnings
            calculateTransactionSummary(result.data);
          } else {
            setCsvPreview(result);
            setCsvFormatError('');
            
            // Calculate transaction summary
            calculateTransactionSummary(result.data);
            
            // Generate NACHA preview if there's data
            if (result.data.length > 0) {
              // Take just first 3 entries for preview
              const previewData = result.data.slice(0, 3);
              const previewNacha = createNachaFile(previewData, companyConfig);
              
              // Get first 10 lines of NACHA content
              const nachaLines = previewNacha.split('\n').slice(0, 10);
              setNachaPreview(nachaLines.join('\n') + 
                (previewNacha.split('\n').length > 10 ? '\n... (more lines not shown in preview)' : ''));
            }
          }
        } catch (err) {
          console.error('Error previewing CSV:', err);
          setCsvPreview(null);
          setCsvFormatError('Error parsing CSV: ' + (err.message || 'Invalid format'));
        }
      };
      reader.readAsText(file);
    }
  };

  // Set company config to default preset on load
  useEffect(() => {
    if (companyPresets.length > 0) {
      const defaultPreset = companyPresets[0];
      setCompanyConfig(defaultPreset.config);
    }
  }, []);
  
  /**
   * Calculates transaction summary statistics from the CSV data
   * Includes total amount, count of credits and debits, etc.
   * 
   * @param {Array} data - The parsed CSV data array
   */
  const calculateTransactionSummary = (data) => {
    if (!data || data.length === 0) {
      setTransactionSummary(null);
      return;
    }
    
    try {
      // Initialize counters
      let totalAmount = 0;
      let debitCount = 0;
      let creditCount = 0;
      let debitAmount = 0;
      let creditAmount = 0;
      
      // Process each transaction
      data.forEach(transaction => {
        const amount = parseFloat(transaction.amount) || 0;
        
        if (transaction.transactionType?.toLowerCase() === 'debit') {
          debitCount++;
          debitAmount += amount;
        } else if (transaction.transactionType?.toLowerCase() === 'credit') {
          creditCount++;
          creditAmount += amount;
        }
        
        totalAmount += amount;
      });
      
      // Set the transaction summary
      setTransactionSummary({
        totalCount: data.length,
        totalAmount: totalAmount.toFixed(2),
        debitCount,
        creditCount,
        debitAmount: debitAmount.toFixed(2),
        creditAmount: creditAmount.toFixed(2)
      });
    } catch (error) {
      console.error('Error calculating transaction summary:', error);
      setTransactionSummary(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!csvFile) {
      setError('Please select a CSV file');
      return;
    }
    
    if (csvFormatError) {
      setError('Please fix the CSV format errors before generating a NACHA file');
      return;
    }
    
    // Show confirmation dialog with transaction totals
    if (transactionSummary) {
      setShowConfirmation(true);
      return;
    }
    
    // If no transaction summary (shouldn't happen normally), continue with generation
    await generateNachaFile();
  };
  
  /**
   * Handles the actual NACHA file generation after confirmation
   */
  const generateNachaFile = async () => {
    setNachaContent('');
    setShowConfirmation(false);
    
    try {
      // Read and parse CSV file
      const text = await readFile(csvFile);
      const result = Papa.parse(text, { 
        header: true, 
        skipEmptyLines: true 
      });

      if (result.errors.length > 0) {
        setError(`CSV parsing error: ${result.errors[0].message}`);
        return;
      }
      
      // Validate required columns
      const requiredColumns = ['transactionId', 'name', 'accountNumber', 'routingNumber', 'amount', 'transactionType'];
      const missingColumns = requiredColumns.filter(col => !result.meta.fields.includes(col));
      
      if (missingColumns.length > 0) {
        setError(`Invalid CSV format. Missing required columns: ${missingColumns.join(', ')}`);
        return;
      }
      
      // Validate data values
      const dataErrors = [];
      result.data.forEach((row, index) => {
        if (!row.transactionId) dataErrors.push(`Row ${index + 1}: Missing Transaction ID`);
        if (!row.accountNumber) dataErrors.push(`Row ${index + 1}: Missing account number`);
        if (!row.routingNumber || row.routingNumber.length !== 9) 
          dataErrors.push(`Row ${index + 1}: Invalid routing number (must be 9 digits)`);
        if (!row.amount || isNaN(parseFloat(row.amount)))
          dataErrors.push(`Row ${index + 1}: Invalid amount`);
        if (!row.transactionType || !['debit', 'credit'].includes(row.transactionType.toLowerCase()))
          dataErrors.push(`Row ${index + 1}: Invalid transaction type (must be 'debit' or 'credit')`);
          
        // Validate Transaction ID length (NACHA spec limits to 15 characters)
        if (row.transactionId && row.transactionId.length > 15) {
          dataErrors.push(`Row ${index + 1}: Transaction ID exceeds maximum length of 15 characters`);
        }
        
        // Validate addenda if present
        if (row.addenda && row.addenda.length > 80) {
          dataErrors.push(`Row ${index + 1}: Addenda text exceeds maximum length of 80 characters`);
        }
      });
      
      if (dataErrors.length > 0) {
        // Show max 3 errors to avoid overwhelming the user
        const errorDisplay = dataErrors.slice(0, 3).join(', ') + 
          (dataErrors.length > 3 ? ` and ${dataErrors.length - 3} more errors` : '');
        setError(`CSV data validation errors: ${errorDisplay}`);
        return;
      }

      // Generate NACHA content
      const content = createNachaFile(result.data, companyConfig);
      setNachaContent(content);
      
      // Update transaction summary with full data
      calculateTransactionSummary(result.data);
    } catch (err) {
      setError(`Error processing file: ${err.message}`);
    }
  };

  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });
  };

  const downloadNachaFile = () => {
    if (!nachaContent) {
      // Show error if trying to download without generating first
      if (!csvFile) {
        setError('Please upload a CSV file before downloading a NACHA file.');
      } else if (csvFormatError) {
        setError('Please fix CSV format errors before downloading a NACHA file.');
      } else {
        setError('Please generate the NACHA file by clicking the "Generate NACHA File" button first.');
      }
      return;
    }

    const blob = new Blob([nachaContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ach-file-${new Date().toISOString().slice(0, 10)}.nacha`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadTemplateCSV = () => {
    const csvContent = `transactionId,name,accountNumber,routingNumber,amount,transactionType,addenda
INV2025001,John Doe,12345678901234567,021000021,100.50,debit,Invoice #12345
VEN2025002,Jane Smith,98765432101234567,031100209,200.75,debit,Payment for services rendered
PAY2025003,Robert Johnson,45678912345678901,026009593,150.25,debit,Medical claim #ABC-123-XYZ`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'nacha-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Generates a complete NACHA formatted file from CSV data
   * 
   * This function orchestrates the entire NACHA file generation process by:
   * 1. Creating the file header with current date and time
   * 2. Creating a batch header with company information
   * 3. Adding entry detail records for each transaction in the CSV
   * 4. Including addenda records where specified
   * 5. Adding batch control records with calculated totals
   * 6. Adding a file control record with overall totals
   * 7. Adding required file padding to comply with NACHA specifications
   * 
   * The function follows the NACHA file structure:
   * - File Header Record (1)
   * - Batch Header Record (5)
   * - Entry Detail Record(s) (6)
   * - Addenda Record(s) (7) - optional
   * - Batch Control Record (8)
   * - File Control Record (9)
   * - File Padding
   * 
   * @param {Array} records - Array of payment records from the parsed CSV
   * @param {Object} config - Company configuration information
   * @param {string} [serviceClassCode] - Optional service class code (automatically determined if not provided)
   * @returns {string} The complete NACHA formatted file content
   */
  function createNachaFile(records, config, serviceClassCode) {
    const today = new Date();
    const fileDate = formatDate(today);
    const fileTime = formatTime(today);
    
    // Calculate total amount
    const totalAmount = records.reduce((sum, record) => {
      return sum + (parseFloat(record.amount) || 0);
    }, 0);
    
    // Count addenda records
    const addendaCount = records.filter(record => record.addenda && record.addenda.trim().length > 0).length;
    
    // Auto-determine service class code based on transaction types in the CSV
    let autoServiceClassCode = serviceClassCode || '225'; // Default to debits only if not provided
    
    // Check if we need to auto-determine the service class code
    if (!serviceClassCode) {
      const hasCredits = records.some(record => record.transactionType?.toLowerCase() === 'credit');
      const hasDebits = records.some(record => record.transactionType?.toLowerCase() === 'debit');
      
      if (hasCredits && hasDebits) {
        autoServiceClassCode = '200'; // Mixed debits and credits
      } else if (hasCredits && !hasDebits) {
        autoServiceClassCode = '220'; // Credits only
      } else if (!hasCredits && hasDebits) {
        autoServiceClassCode = '225'; // Debits only
      }
    }
    
    // Build NACHA file
    let nacha = '';
    
    // File Header Record
    nacha += fileHeader(config, fileDate, fileTime);
    
    // Batch Header Record
    nacha += batchHeader(config, autoServiceClassCode, fileDate);
    
    // Entry Detail Records with Addenda Records
    records.forEach((record, index) => {
      nacha += entryDetail(record, index + 1, config);
    });
    
    // Batch Control Record - include addenda in entry count
    nacha += batchControl(records.length + addendaCount, Math.round(totalAmount * 100), config, autoServiceClassCode);
    
    // File Control Record - include addenda in entry count
    nacha += fileControl(1, records.length + addendaCount, Math.round(totalAmount * 100));
    
    // File padding
    nacha += '9'.repeat(94) + '\n';
    
    return nacha;
  }

  /**
   * Generates a NACHA File Header Record (Record Type 1)
   * 
   * Creates the first line of the NACHA file that includes:
   * - Destination and origin financial institution information
   * - File creation date and time
   * - Format specifications
   * - Financial institution names
   * 
   * @param {Object} config - Company configuration information
   * @param {string} fileDate - Formatted file creation date (YYMMDD)
   * @param {string} fileTime - Formatted file creation time (HHMM)
   * @returns {string} Formatted file header record
   */
  function fileHeader(config, fileDate, fileTime) {
    return '1' +                                                // Record Type Code
      '01' +                                                    // Priority Code
      ` ${config.immediateDestination.padEnd(9, ' ')}` +       // Destination
      ` ${config.immediateOrigin.padEnd(9, ' ')}` +            // Origin
      fileDate +                                                // File Date
      fileTime +                                                // File Time
      'A' +                                                     // File ID Modifier
      '094' +                                                   // Record Size
      '10' +                                                    // Blocking Factor
      '1' +                                                     // Format Code
      'CHASE BANK'.padEnd(23, ' ') +                           // Destination Name
      config.companyName.padEnd(23, ' ') +                     // Origin Name
      ' '.repeat(8) +                                          // Reference Code
      '\n';
  }

  /**
   * Generates a NACHA Batch Header Record (Record Type 5)
   * 
   * Creates the batch header record that includes:
   * - Company name and identification
   * - Service class code (debit/credit designation)
   * - Standard Entry Class code (PPD for consumer payments)
   * - Entry description and effective date
   * 
   * @param {Object} config - Company configuration information
   * @param {string} serviceClassCode - Service class code (200, 220, or 225)
   * @param {string} fileDate - Formatted file creation date (YYMMDD)
   * @returns {string} Formatted batch header record
   */
  function batchHeader(config, serviceClassCode, fileDate) {
    return '5' +                                               // Record Type Code
      serviceClassCode +                                       // Service Class Code
      config.companyName.padEnd(16, ' ') +                    // Company Name
      ' '.repeat(20) +                                        // Company Discretionary Data
      config.companyIdentification.padEnd(10, ' ') +          // Company ID
      'PPD' +                                                 // Standard Entry Class
      'PAYMENT'.padEnd(10, ' ') +                            // Entry Description
      ' '.repeat(6) +                                         // Company Descriptive Date
      fileDate +                                              // Effective Entry Date
      ' '.repeat(3) +                                         // Settlement Date
      '1' +                                                   // Originator Status
      config.originatingDFI.substring(0, 8) +                // Originating DFI
      '0000001' +                                            // Batch Number
      '\n';
  }

  /**
   * Generates a NACHA Entry Detail Record (Record Type 6)
   * 
   * Creates an entry detail record for each transaction that includes:
   * - Transaction type (debit or credit)
   * - Account and routing information
   * - Transaction amount
   * - Individual ID (transactionId)
   * - Individual name
   * - Trace number for tracking
   * - Addenda indicator and associated addenda record if present
   * 
   * @param {Object} record - Single payment record from CSV data
   * @param {number} traceNumber - Sequential trace number for the entry
   * @param {Object} config - Company configuration information
   * @returns {string} Formatted entry detail record with optional addenda
   */
  function entryDetail(record, traceNumber, config) {
    const transactionCode = record.transactionType?.toLowerCase() === 'credit' ? '22' : '27';
    const amount = Math.round(parseFloat(record.amount || 0) * 100);
    const hasAddenda = record.addenda && record.addenda.trim().length > 0;
    
    let entryDetail = '6' +                                     // Record Type Code
      transactionCode +                                         // Transaction Code
      (record.routingNumber || '').substring(0, 9) +            // Routing Number
      (record.accountNumber || '').padEnd(17, ' ') +            // Account Number
      amount.toString().padStart(10, '0') +                     // Amount
      (record.transactionId || '').toString().padEnd(15, ' ') +  // Transaction ID Number
      (record.name || '').substring(0, 22).padEnd(22, ' ') +    // Individual Name
      '  ' +                                                    // Discretionary Data
      (hasAddenda ? '1' : '0') +                                // Addenda Indicator (1 if addenda exists)
      config.originatingDFI.substring(0, 8) +                   // Trace Number (Routing)
      traceNumber.toString().padStart(7, '0') +                 // Trace Sequence
      '\n';
      
    // Add addenda record if it exists
    if (hasAddenda) {
      entryDetail += addendaRecord(record.addenda, traceNumber, config);
    }
    
    return entryDetail;
  }
  
  /**
   * Generates a NACHA Addenda Record (Record Type 7)
   * 
   * Creates an addenda record (Type 05) that contains additional
   * payment-related information such as invoice numbers or payment references.
   * Each addenda record is linked to its parent entry detail record.
   * 
   * @param {string} addendaText - Additional payment information text
   * @param {number} traceNumber - Trace number matching the parent entry detail
   * @param {Object} config - Company configuration information
   * @returns {string} Formatted addenda record
   */
  function addendaRecord(addendaText, traceNumber, config) {
    // Per NACHA specification, addenda record type '7'
    return '7' +                                               // Record Type Code
      '05' +                                                   // Addenda Type Code (05 for POS, PPD, etc.)
      addendaText.substring(0, 80).padEnd(80, ' ') +          // Payment Related Information
      '0001' +                                                // Addenda Sequence Number
      config.originatingDFI.substring(0, 8) +                 // Entry Detail Sequence Number (Trace) 
      traceNumber.toString().padStart(7, '0') +               // Matches the trace number from the entry detail
      '\n';
  }

  /**
   * Generates a NACHA Batch Control Record (Record Type 8)
   * 
   * Creates a batch control record that contains:
   * - Entry counts and hash totals
   * - Total monetary amounts for the batch
   * - Company identification
   * - Originating financial institution information
   * 
   * @param {number} entryCount - Count of all entry detail and addenda records
   * @param {number} totalAmount - Total amount in cents for all transactions
   * @param {Object} config - Company configuration information
   * @param {string} serviceClassCode - Service class code (200, 220, or 225)
   * @returns {string} Formatted batch control record
   */
  function batchControl(entryCount, totalAmount, config, serviceClassCode) {
    return '8' +                                              // Record Type Code
      serviceClassCode +                                      // Service Class Code
      entryCount.toString().padStart(6, '0') +                // Entry Count
      '0'.padStart(10, '0') +                                 // Entry Hash
      totalAmount.toString().padStart(12, '0') +              // Debit Amount
      '0'.padStart(12, '0') +                                 // Credit Amount
      config.companyIdentification.padEnd(10, ' ') +          // Company ID
      ' '.repeat(19) +                                        // Authentication Code
      ' '.repeat(6) +                                         // Reserved
      config.originatingDFI.substring(0, 8) +                 // Originating DFI
      '0000001' +                                             // Batch Number
      '\n';
  }

  /**
   * Generates a NACHA File Control Record (Record Type 9)
   * 
   * Creates the final control record for the NACHA file that contains:
   * - Batch count and block count
   * - Total entry count for all batches
   * - Entry hash and total monetary amounts
   * 
   * @param {number} batchCount - Number of batch records in the file
   * @param {number} entryCount - Total count of all entry detail and addenda records
   * @param {number} totalAmount - Total amount in cents for all transactions
   * @returns {string} Formatted file control record
   */
  function fileControl(batchCount, entryCount, totalAmount) {
    return '9' +                                             // Record Type Code
      batchCount.toString().padStart(6, '0') +               // Batch Count
      '000001' +                                             // Block Count
      entryCount.toString().padStart(8, '0') +               // Entry Count
      '0'.padStart(10, '0') +                                // Entry Hash
      totalAmount.toString().padStart(12, '0') +             // Total Debit
      '0'.padStart(12, '0') +                                // Total Credit
      ' '.repeat(39) +                                       // Reserved
      '\n';
  }

  /**
   * Formats a date object into NACHA format (YYMMDD)
   * 
   * @param {Date} date - JavaScript Date object
   * @returns {string} Formatted date string (YYMMDD)
   */
  function formatDate(date) {
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return year + month + day;
  }

  /**
   * Formats a date object's time into NACHA format (HHMM)
   * 
   * @param {Date} date - JavaScript Date object
   * @returns {string} Formatted time string (HHMM)
   */
  function formatTime(date) {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return hours + minutes;
  }
  
  /**
   * Handles user authentication
   * 
   * Validates the entered password against the hardcoded value "Denver1234".
   * If valid, sets the authentication state and stores it in session storage.
   * If invalid, displays an error message.
   * 
   * @param {Event} e - Form submission event
   */
  const handleAuthentication = (e) => {
    e.preventDefault();
    if (password === 'Denver1234') {
      setIsAuthenticated(true);
      sessionStorage.setItem('nacha_authenticated', 'true');
      setAuthError('');
    } else {
      setAuthError('Invalid password');
    }
  };
  
  /**
   * Handles user logout
   * 
   * Clears the authentication state from both app state and session storage,
   * and resets the password field.
   */
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('nacha_authenticated');
    setPassword('');
  };

  // Authentication form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        {showManual && <ManualPage onClose={() => {
          setShowManual(false);
          // Prevent error message appearing on manual close
          setError('');
        }} initialSection={initialHelpSection} />}
        <div className="mb-10">
          <img 
            src="/logo.svg" 
            alt="Property Llama Capital Logo" 
            className="h-20 w-auto"
          />
        </div>
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8">
          <h1 className="text-2xl font-bold mb-6 text-gray-800 text-center">Property Llama Capital<br></br>NACHA Converter Login</h1>
          
          <form onSubmit={handleAuthentication} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            {authError && (
              <div className="p-3 bg-red-100 border-l-4 border-red-500 text-red-700 text-sm">
                {authError}
              </div>
            )}
            
            <button
              type="submit"
              className="w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Login
            </button>
          </form>
          
          <p className="mt-6 text-xs text-center text-gray-500">
            Property Llama Capital - Secure Access
          </p>
          <p className="mt-2 text-xs text-center text-gray-400">
            Developed by Richard McGirr
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {showManual && <ManualPage onClose={() => {
        setShowManual(false);
        // Prevent error message appearing on manual close
        setError('');
      }} initialSection={initialHelpSection} />}
      
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-6">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">Property Llama Capital CSV to NACHA Converter</h1>
            <div className="flex space-x-4">
              <button 
                onClick={() => {
                  setInitialHelpSection('');
                  setShowManual(true);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Help</span>
              </button>
              <button 
                onClick={handleLogout}
                className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
          <p className="text-gray-600 mt-1">This application converts CSV files into NACHA format for ACH transfers, streamlining the process of distributing funds to limited partners. Simply upload your CSV file with payment details to generate bank-compliant ACH instructions for Chase Bank.</p>
        </div>
        
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-blue-700 rounded-r">
          <h2 className="font-medium mb-2 text-center">How to use this tool:</h2>
          <div className="overflow-x-auto">
            <div className="flex flex-col sm:flex-row justify-center gap-6 sm:gap-10 text-sm">
              <div className="flex flex-col items-center">
                <span className="flex-shrink-0 font-bold w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-2">1</span>
                <span>Upload your CSV file</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="flex-shrink-0 font-bold w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-2">2</span>
                <span>Generate NACHA file</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="flex-shrink-0 font-bold w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-2">3</span>
                <span>Download file</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="flex-shrink-0 font-bold w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-2">4</span>
                <span>Upload to bank</span>
              </div>
            </div>
          </div>
          <div className="text-center mt-3">
            <button 
              onClick={() => setShowManual(true)}
              className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded hover:bg-blue-200"
            >
              View complete user manual
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* File Upload Section */}
          <div>
            <div className="flex items-center mb-3">
              <h2 className="text-lg font-semibold">CSV File Upload</h2>
              <span className="ml-2 bg-yellow-200 text-yellow-800 text-xs font-medium px-2 py-0.5 rounded">Required</span>
              <button 
                onClick={() => {
                  setInitialHelpSection('csv-format');
                  setShowManual(true);
                }}
                className="ml-2 text-blue-600 hover:text-blue-800"
                title="View CSV format help"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="mb-2"
              />
              <p className="text-sm text-gray-500 mb-2">
                Upload a CSV file with required columns: transactionId, name, accountNumber, routingNumber, amount, transactionType.
                The addenda column is optional for payment reference information.
              </p>
              <div className="mt-3 bg-gray-50 p-3 rounded text-xs text-gray-600 text-left">
                <p className="font-semibold mb-1">Your CSV should look like this:</p>
                <code>transactionId,name,accountNumber,routingNumber,amount,transactionType,addenda</code><br/>
                <code>INV2025001,John Doe,12345678901234567,021000021,100.50,debit,Invoice #12345</code>
              </div>
              <button
                type="button"
                onClick={downloadTemplateCSV}
                className="mt-4 text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 py-1 px-3 rounded"
              >
                Download Template CSV
              </button>
            </div>
            {csvFile && (
              <div className="mt-2 flex items-center text-sm text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Selected file: <span className="font-medium">{csvFile.name}</span>
              </div>
            )}
            
            {csvFormatError && (
              <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm">
                <div className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium">CSV Format Error</p>
                    <p>{csvFormatError}</p>
                    <p className="mt-1 text-xs">Please make sure your CSV has all required columns: id, name, accountNumber, routingNumber, amount, transactionType</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* CSV Preview Section with Transaction Summary */}
          {csvPreview && (
            <div className="mt-4">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-4">
                <div>
                  <h2 className="text-lg font-semibold mb-2">CSV Preview</h2>
                </div>
                
                {transactionSummary && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 min-w-[250px]">
                    <h3 className="text-sm font-semibold text-blue-800 mb-2">Transaction Summary</h3>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div className="text-blue-700">Total Transactions:</div>
                      <div className="font-semibold text-blue-900">{transactionSummary.totalCount}</div>
                      
                      <div className="text-blue-700">Total Amount:</div>
                      <div className="font-semibold text-blue-900">${transactionSummary.totalAmount}</div>
                      
                      <div className="text-blue-700">Debits:</div>
                      <div className="font-semibold text-blue-900">
                        {transactionSummary.debitCount} (${transactionSummary.debitAmount})
                      </div>
                      
                      <div className="text-blue-700">Credits:</div>
                      <div className="font-semibold text-blue-900">
                        {transactionSummary.creditCount} (${transactionSummary.creditAmount})
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="overflow-x-auto border rounded">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {csvPreview.meta.fields.map((field) => (
                        <th 
                          key={field}
                          className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {field}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {csvPreview.data.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {csvPreview.meta.fields.map((field) => (
                          <td key={field} className="px-3 py-2 text-xs text-gray-500">
                            {row[field]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Showing first {csvPreview.data.length} rows of the CSV file
              </p>
            </div>
          )}
          
          {/* NACHA Preview Section */}
          {nachaPreview && !csvFormatError && (
            <div className="mt-4">
              <h2 className="text-lg font-semibold mb-2">NACHA Preview</h2>
              <div className="bg-gray-50 border rounded p-3 overflow-x-auto">
                <p className="text-xs text-gray-500 mb-2">
                  Preview of what your NACHA file will look like (first 10 lines):
                </p>
                <pre className="text-xs font-mono whitespace-pre text-gray-700" style={{ maxHeight: '200px' }}>
                  {nachaPreview}
                </pre>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                This is just a preview. Click "Generate NACHA File" to create the complete file.
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className={`px-6 py-3 rounded-lg flex items-center font-medium shadow-sm ${
                !csvFile || csvFormatError 
                  ? 'bg-blue-400 text-white/80 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              disabled={!csvFile || csvFormatError}
              title={csvFormatError ? 'Please fix CSV format errors first' : (!csvFile ? 'Please upload a CSV file first' : 'Generate NACHA file')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Generate NACHA File
            </button>
          </div>
        </form>

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-r">
            <div className="flex">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
                <button 
                  onClick={() => {
                    setInitialHelpSection('troubleshooting');
                    setShowManual(true);
                  }} 
                  className="text-xs underline mt-1"
                >
                  Need help? View troubleshooting guide
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Result Section */}
        {nachaContent && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
              <div>
                <h2 className="text-lg font-semibold text-green-800">NACHA File Generated Successfully!</h2>
                <p className="text-sm text-green-700 mt-1">
                  Your file is ready to download and upload to your bank's ACH portal.
                </p>
              </div>
              <button
                onClick={downloadNachaFile}
                disabled={!nachaContent || csvFormatError}
                className={`px-4 py-2 rounded-lg flex items-center shadow-sm whitespace-nowrap ${
                  !nachaContent || csvFormatError 
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download NACHA File
              </button>
            </div>
            <div className="bg-white border border-green-200 rounded p-3 overflow-x-auto">
              <p className="text-xs text-gray-500 mb-2">Preview of your NACHA file:</p>
              <pre className="text-xs text-gray-700 font-mono overflow-x-auto whitespace-pre" style={{ maxHeight: '200px' }}>
                {nachaContent}
              </pre>
            </div>
            <div className="mt-3 text-sm text-green-700">
              <p className="font-medium">Next steps:</p>
              <ol className="list-decimal pl-5 mt-1 space-y-1 text-sm">
                <li>Download the NACHA file using the button above</li>
                <li>Log in to Chase for Business or Chase Connect at chase.com/business</li>
                <li>From the Pay & transfer menu, choose ACH payment services, then ACH file upload</li>
                <li>Click "Choose a file" button and select your downloaded .nacha file</li>
                <li>Click "Next" to validate, then review and submit for processing</li>
                <li>Save the confirmation for your records</li>
              </ol>
              <button 
                onClick={() => {
                  setInitialHelpSection('chase-submission');
                  setShowManual(true);
                }}
                className="text-xs underline mt-2 text-green-800"
              >
                View detailed Chase submission instructions
              </button>
            </div>
          </div>
        )}

        {/* CSV Format Help */}
        <div className="mt-8 pt-6 border-t">
          <h2 className="text-lg font-semibold mb-2">CSV Format Requirements</h2>
          <p className="text-sm text-gray-600 mb-3">
            Your CSV file must include the following columns:
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Column</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Description</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap">Example</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap">transactionId</td>
                    <td className="px-3 py-2 text-gray-600">
                      Transaction ID
                      <button
                        onClick={() => {
                          setInitialHelpSection('transaction-id');
                          setShowManual(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 ml-2 align-text-bottom"
                        title="View transaction ID help"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">INV2025001</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap">name</td>
                    <td className="px-3 py-2 text-gray-600">Recipient's name</td>
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">John Doe</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap">accountNumber</td>
                    <td className="px-3 py-2 text-gray-600">Bank account number</td>
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">12345678901234</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap">routingNumber</td>
                    <td className="px-3 py-2 text-gray-600">ABA routing number</td>
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">021000021</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap">amount</td>
                    <td className="px-3 py-2 text-gray-600">Payment amount</td>
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">100.50</td>
                    <td></td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap">transactionType</td>
                    <td className="px-3 py-2 text-gray-600">Type (debit/credit)</td>
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">debit</td>
                    <td></td>
                  </tr>
                  <tr className="bg-blue-50">
                    <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap">addenda</td>
                    <td className="px-3 py-2 text-gray-600">
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">Optional</span> 
                      Payment reference info
                      <button
                        onClick={() => {
                          setInitialHelpSection('addenda');
                          setShowManual(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 ml-2 align-text-bottom"
                        title="View addenda records help"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">Invoice #12345</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="flex justify-center mt-4">
              <button
                type="button"
                onClick={downloadTemplateCSV}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm rounded flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Template CSV
              </button>
            </div>
          </div>
          

        </div>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t text-center">
          <div className="flex justify-center mb-3">
            <img 
              src="/logo.svg" 
              alt="Property Llama Capital Logo" 
              className="h-8 w-auto"
            />
          </div>
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} Property Llama Capital
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Developed by Richard McGirr
          </p>
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      {showConfirmation && transactionSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <div className="p-6">
              <h2 className="text-lg font-bold mb-4">Confirm NACHA File Generation</h2>
              <div className="bg-yellow-50 p-4 rounded border border-yellow-200 mb-4">
                <h3 className="text-sm font-semibold text-yellow-800 mb-2">Transaction Summary</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div className="text-yellow-700">Total Transactions:</div>
                  <div className="font-semibold text-yellow-900">{transactionSummary.totalCount}</div>
                  
                  <div className="text-yellow-700">Total Amount:</div>
                  <div className="font-semibold text-yellow-900">${transactionSummary.totalAmount}</div>
                  
                  <div className="text-yellow-700">Debits:</div>
                  <div className="font-semibold text-yellow-900">
                    {transactionSummary.debitCount} (${transactionSummary.debitAmount})
                  </div>
                  
                  <div className="text-yellow-700">Credits:</div>
                  <div className="font-semibold text-yellow-900">
                    {transactionSummary.creditCount} (${transactionSummary.creditAmount})
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Please confirm that you want to generate a NACHA file with the transactions summarized above.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={generateNachaFile}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Confirm & Generate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;